import Axios from "axios"

import { getCachedInventory, setCachedInventory } from "./inventoryCache"
import { withRetry } from "./retry"

export const getAutomaticDiscounts = () => {
  return Axios.post("/api/postAutomaticDiscounts", {})
    .then((response) => response.data)
    .catch((error) => {
      console.error(error)
    })
}

export const postBulkOrder = (data, callback) => {
  return Axios({
    data,
    headers: { Accept: "*/*", "Content-Type": "application/json" },
    method: "POST",
    url: "/api/postBulkOrder",
  })
    .then((response) => response)
    .catch((error) => {
      console.log(error)
    })
    .finally(() => {
      callback()
    })
}

export const getFenixShippingEstimate = (
  buyerZipCode: string,
  skus: Array<{ quantity: number; sku: string }>
) => {
  return Axios.post("/api/postFenixShippingEstimate", {
    buyerZipCode,
    skus,
  })
    .then((response) => response.data)
    .catch((error) => {
      console.error(error)
    })
}

const pending = new Map<string, Promise<any>>()

export const getInventoryByLocation = (skus: string[]) => {
  // Stable key - use same format as hook for consistency
  const uniqueSkus = [...new Set(skus)].sort()
  const cacheKey = uniqueSkus.join("|")
  const dedupeKey = JSON.stringify(uniqueSkus)

  // Check in-flight requests first
  if (pending.has(dedupeKey)) {
    return pending.get(dedupeKey)!
  }

  // Check cache
  const cached = getCachedInventory(cacheKey)
  if (cached) {
    return Promise.resolve(cached)
  }

  const CHUNK_SIZE = 1000

  // If SKUs are within limit, make a single request
  if (uniqueSkus.length <= CHUNK_SIZE) {
    const response = Axios.post("/api/postInventoryByLocation", { skus })
      .then((response) => {
        // Cache the raw response data
        if (response.data) {
          setCachedInventory(cacheKey, response.data)
        }
        return response.data
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => pending.delete(dedupeKey))

    pending.set(dedupeKey, response)
    return response
  }

  // Split SKUs into chunks and make parallel requests
  const chunks: string[][] = []
  for (let i = 0; i < uniqueSkus.length; i += CHUNK_SIZE) {
    chunks.push(uniqueSkus.slice(i, i + CHUNK_SIZE))
  }

  const response = Promise.all(
    chunks.map((chunk) =>
      Axios.post("/api/postInventoryByLocation", { skus: chunk })
        .then((response) => response.data)
        .catch((error) => {
          console.error(error)
          return []
        })
    )
  )
    .then((results) => {
      // Merge all chunk results into a single array
      const merged = results.flat()
      // Cache the merged response
      if (merged) {
        setCachedInventory(cacheKey, merged)
      }
      return merged
    })
    .catch((error) => {
      console.error(error)
    })
    .finally(() => pending.delete(dedupeKey))

  pending.set(dedupeKey, response)
  return response
}

export const getOrder = (data) => {
  return Axios.post("/api/postOrder", data)
    .then((response) => response.data)
    .catch((error) => {
      console.error(error)
    })
}

export const postOutOfStock = (data, callback) => {
  Axios({
    data,
    headers: { Accept: "*/*", "Content-Type": "application/json" },
    method: "POST",
    url: "/api/postOutOfStock",
  })
    .then((response) => response)
    .catch((error) => {
      console.log(error)
    })
    .finally(() => {
      callback()
    })
}

export const postSubscribeNewsletter = (data, callback) => {
  return Axios({
    data,
    headers: { "Content-Type": "application/json" },
    method: "POST",
    url: "/api/postSubscribeNewsletter",
  })
    .then((response) => response)
    .catch((error) => {
      console.log(error)
    })
    .finally(() => {
      callback()
    })
}

export const postReturn = (data, callback) => {
  return Axios({
    data,
    headers: { Accept: "*/*", "Content-Type": "application/json" },
    method: "POST",
    url: "/api/postReturn",
  })
    .then((response) => response)
    .catch((error) => {
      console.log(error)
    })
    .finally(() => {
      callback()
    })
}

export async function postReturnWithRetry(data: any, cb?: any) {
  return withRetry(() => postReturn(data, cb), 3, 2000)
}

export const postWarrantyClaim = (data, callback) => {
  return Axios({
    data,
    headers: { Accept: "*/*", "Content-Type": "application/json" },
    method: "POST",
    url: "/api/postWarrantyClaim",
  })
    .then((response) => response)
    .catch((error) => {
      console.log(error)
    })
    .finally(() => {
      callback()
    })
}

export async function postWarrantyClaimWithRetry(data: any, cb?: any) {
  return withRetry(() => postWarrantyClaim(data, cb), 3, 2000)
}

export const getGeo = () => {
  return Axios({
    method: "GET",
    url: "/api/getGeo",
  })
    .then((response) => response)
    .catch((error) => {
      console.log(error)
    })
}

export const getRewardsBalance = (data) => {
  return Axios({
    data,
    method: "POST",
    url: "/api/getRewardsBalance",
  })
    .then((response) => response)
    .catch((error) => {
      console.log(error)
    })
}

export const setCartId = (cartId) => {
  return Axios({
    data: { cartId },
    headers: { "Content-Type": "application/json" },
    method: "POST",
    url: "/api/setCartId",
  })
    .then((response) => response)
    .catch((error) => {
      console.log(error)
    })
}
